import { Link } from "react-router-dom";
import { Clock, ArrowRight } from "lucide-react";

interface BlogCardProps {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  category: string;
  categoryColor?: string;
  featured?: boolean;
}

const BlogCard = ({
  slug,
  title,
  excerpt,
  date,
  readTime,
  category,
  categoryColor = "from-neon-purple to-neon-pink",
  featured = false,
}: BlogCardProps) => {
  return (
    <Link
      to={`/blog/${slug}`}
      className={`group block p-6 rounded-2xl glass-morphism border transition-all duration-300 hover:scale-[1.02] relative overflow-hidden ${
        featured 
          ? "border-neon-purple/40 hover:border-neon-purple md:col-span-2 hover:shadow-xl hover:shadow-neon-purple/20" 
          : "border-border/50 hover:border-neon-cyan/50 hover:shadow-xl hover:shadow-neon-cyan/10"
      }`}
    >
      {/* Hover glow effect */}
      <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none ${
        featured 
          ? "bg-gradient-to-br from-neon-purple/10 via-transparent to-neon-pink/10"
          : "bg-gradient-to-br from-neon-cyan/5 via-transparent to-neon-purple/5"
      }`} />
      
      {/* Category Badge */}
      <div className="mb-4 relative z-10">
        <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r ${categoryColor} text-white shadow-md`}>
          {category}
        </span>
      </div>

      {/* Title */}
      <h3 className={`font-heading font-bold mb-3 group-hover:text-neon-purple transition-colors relative z-10 ${
        featured ? "text-2xl md:text-3xl" : "text-xl"
      }`}>
        {title}
      </h3>

      {/* Excerpt */}
      <p className={`text-muted-foreground mb-4 line-clamp-2 relative z-10 ${
        featured ? "text-base md:line-clamp-3" : "text-sm"
      }`}>
        {excerpt}
      </p>

      {/* Meta */}
      <div className="flex items-center justify-between text-xs text-muted-foreground relative z-10">
        <div className="flex items-center gap-4">
          <span>{date}</span>
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>{readTime}</span>
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-neon-purple opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
      </div>
    </Link>
  );
};

export default BlogCard;
